import React, { useEffect, useRef } from 'react';

/**
 * User guide modal for the CMA workflow.
 *
 * Props:
 *   show        — boolean, whether the modal is visible
 *   onClose     — callback to close the modal
 *   section     — initial section to scroll to: 'expected_returns' | 'covariance'
 */
const CMAUserGuideModal = ({ show, onClose, section = 'expected_returns' }) => {
  const bodyRef = useRef(null);

  // Scroll to the relevant section when opened
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`cma-guide-${section}`);
      if (el && bodyRef.current) {
        bodyRef.current.scrollTop = el.offsetTop - bodyRef.current.offsetTop - 8;
      }
    }, 50);
    return () => clearTimeout(t);
  }, [show, section]);

  if (!show) return null;

  const sectionStyle = {
    paddingTop: '1rem',
    paddingBottom: '0.5rem',
    borderTop: '2px solid var(--secondary-color)',
    marginTop: '1.5rem',
  };
  const h5Style = { color: 'var(--primary-color)', fontWeight: 700 };
  const h6Style = { color: 'var(--primary-color)', fontWeight: 600, marginTop: '1rem' };
  const codeStyle = { background: '#f4f4f4', padding: '1px 5px', borderRadius: '3px', fontSize: '0.85em' };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{ backgroundColor: 'rgba(7, 32, 64, 0.55)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header" style={{ backgroundColor: 'var(--primary-color)', color: '#fff' }}>
            <h5 className="modal-title">Capital Market Assumptions — User Guide</h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              aria-label="Close"
              onClick={onClose}
            />
          </div>
          <div className="modal-body" ref={bodyRef} style={{ maxHeight: '75vh' }}>

            {/* ─── Overview ──────────────────────────────────────────────── */}
            <h5 style={h5Style}>Overview</h5>
            <p>
              The Capital Market Assumptions (CMA) module is where you define forward-looking
              expectations for asset-class returns, volatilities and correlations — the inputs
              that every portfolio optimisation, stress test and Monte Carlo simulation across
              the Portal consumes.
            </p>
            <p>
              The module is built around <strong>scenarios</strong>. Each scenario is a complete,
              self-contained CMA set: a name, expected returns and volatilities for every
              sub-asset class (SAC), and a covariance matrix. You can have many scenarios (e.g.
              <em> Base Case 2026</em>, <em> Sticky Inflation</em>, <em> AI Economy</em>), and
              every consumer of CMAs lets you select which scenario to use. One scenario is
              always marked as the <strong>default</strong>; consumers that don't specify a
              scenario fall back to it.
            </p>
            <p>
              Two tabs in this editor make up the scenario inputs:
            </p>
            <ul>
              <li><strong>Expected Returns</strong> — annual return and annual volatility per SAC.</li>
              <li><strong>Covariance Matrix</strong> — the correlation / covariance structure between SACs.</li>
            </ul>
            <p>
              Other tabs (Index Mappings, Sub Asset Classes, Broad Asset Classes)
              configure the taxonomy and the historical data sources that
              feed into the compute pipelines. Growth/Defensive classification lives
              on the Broad Asset Classes tab.
            </p>

            <h6 style={h6Style}>The flow at a glance</h6>
            <ol>
              <li><strong>Index Mappings</strong> — each SAC is mapped to a column in the tactical DB's monthly index returns table. Saving the mapping pulls the historical series into <code style={codeStyle}>monthly_historic_returns</code>.</li>
              <li><strong>Sub Asset Classes</strong> — defines each SAC's broad-class membership, growth/defensive classification, income yield (or the <em>Income = Return</em> tick, which makes the class income-only: income equals the default scenario's expected return and growth is zero), and (optionally) <em>backfill proxies</em> used when its history is shorter than other assets.</li>
              <li><strong>Macro Regimes</strong> (in a separate admin tab) — labels date windows by macro regime (e.g. <em>Sticky Inflation 2021–24</em>). Used by the covariance compute pipeline for regime-blending.</li>
              <li><strong>Expected Returns</strong> — you enter the forward-looking return and vol for each SAC, per scenario.</li>
              <li><strong>Covariance Matrix</strong> — either edited manually, pasted from Excel, or computed from history using the pipeline described below.</li>
            </ol>

            {/* ─── Scenarios ─────────────────────────────────────────────── */}
            <div id="cma-guide-scenarios" style={sectionStyle}>
              <h5 style={h5Style}>Scenarios</h5>
              <p>
                A scenario is the unit of work. Use the scenario selector at the top of both the
                Expected Returns and Covariance Matrix tabs to switch between them.
              </p>

              <h6 style={h6Style}>Creating a scenario</h6>
              <ul>
                <li><strong>+ New Scenario</strong> opens a modal where you name the scenario and optionally clone the returns + covariance from an existing one. Cloning is the right starting point if you're modelling a "tilt" off an existing base case.</li>
                <li><strong>Import from Blender DB</strong> pulls the current values from the offline blender pipeline as a new scenario. Useful for snapshotting whatever the existing Portfolio Modelling Python script produced.</li>
                <li><strong>Download Template</strong> gives you a complete Excel workbook (metadata + expected returns + covariance sheets) pre-filled with the current SAC list. Fill it in, then <strong>Upload Scenario</strong> to create a complete scenario in one shot. The workbook upload runs the same PSD validation as the manual edit flow.</li>
              </ul>

              <h6 style={h6Style}>Default scenario</h6>
              <p>
                Exactly one scenario should be marked default — this is the one any downstream
                consumer falls back to when no scenario is specified. Click <strong>Set as Default</strong>
                on a scenario to promote it; the previous default is demoted automatically.
                The default cannot be deleted.
              </p>
            </div>

            {/* ─── Expected Returns ──────────────────────────────────────── */}
            <div id="cma-guide-expected_returns" style={sectionStyle}>
              <h5 style={h5Style}>Expected Returns tab</h5>
              <p>
                For each SAC, enter the <strong>annual expected return</strong> and the
                <strong> annual volatility</strong> (standard deviation of annual returns).
                Both are entered as percentages but stored as decimals — the UI handles the
                conversion automatically.
              </p>

              <h6 style={h6Style}>How the numbers are used</h6>
              <ul>
                <li>
                  <strong>Annual return</strong> feeds the geometric/arithmetic return inputs
                  in Portfolio Lab risk decomposition, the retirement calculator's wealth
                  projection, and any optimiser that maximises expected return.
                </li>
                <li>
                  <strong>Annual volatility</strong> is the diagonal of the covariance matrix
                  by construction (whether you enter the covariance manually or compute it from
                  history). Changing a vol here will recompute every covariance cell involving
                  that SAC the next time the matrix is built, because
                  <code style={codeStyle}>cov<sub>ij</sub> = corr<sub>ij</sub> &times; vol<sub>i</sub> &times; vol<sub>j</sub></code>.
                </li>
              </ul>

              <h6 style={h6Style}>Input shortcuts</h6>
              <ul>
                <li>
                  <strong>Paste from Excel</strong> — copy three columns (SAC, return %, vol %)
                  including the header row from any spreadsheet. The parser auto-detects whether
                  values are decimals (0.075) or percentages (7.5). Existing rows are
                  updated; unknown SACs are appended.
                </li>
                <li>
                  <strong>Download Template</strong> — exports the current scenario's returns
                  as a CSV, useful for offline editing or bulk-comparing scenarios.
                </li>
              </ul>

              <h6 style={h6Style}>Saving</h6>
              <p>
                The <strong>Save Returns</strong> button writes both expected returns and the
                covariance matrix (so vol changes propagate consistently). If the resulting
                matrix is not positive semi-definite (PSD), the save is blocked until you fix
                the matrix — usually via the <em>Fix Matrix</em> button on the Covariance Matrix
                tab.
              </p>
            </div>

            {/* ─── Covariance Matrix ─────────────────────────────────────── */}
            <div id="cma-guide-covariance" style={sectionStyle}>
              <h5 style={h5Style}>Covariance Matrix tab</h5>
              <p>
                This is the most important and the most subtle input. Bad correlations lead to
                bad optimisations far more reliably than bad returns do, and historical sample
                correlations are notoriously noisy at the asset-class level. This module gives
                you three ways to build the matrix:
              </p>
              <ol>
                <li><strong>Manual editing</strong> in the lower-triangle grid.</li>
                <li><strong>Paste a matrix</strong> from Excel (full N&times;N grid, auto-detects correlation vs covariance).</li>
                <li><strong>Compute from History</strong> — the recommended approach. Full pipeline below.</li>
              </ol>

              <h6 style={h6Style}>Editing manually</h6>
              <ul>
                <li>You edit the <strong>lower triangle</strong> only — the upper triangle mirrors automatically (the matrix must be symmetric).</li>
                <li>Toggle between <strong>Correlation</strong> view (values −1 to +1) and <strong>Covariance</strong> view (shown as basis points × 10,000 for readability).</li>
                <li>Correlation cells are colour-coded: green for positive, yellow for negative, intensity scales with magnitude.</li>
                <li>When you change a correlation, the covariance is auto-computed using the SAC vols from Expected Returns. So vols and correlations stay in sync.</li>
              </ul>

              <h6 style={h6Style}>The PSD constraint</h6>
              <p>
                A valid covariance matrix must be <strong>positive semi-definite</strong> (PSD):
                every portfolio variance computed from it must be non-negative. Random edits
                often violate this — for example, you can't have EQ_AU ↔ EQ_WORLD = 0.9,
                EQ_AU ↔ BND = −0.9 and EQ_WORLD ↔ BND = +0.9 simultaneously, because there's
                no real return distribution consistent with that. The Portal continuously checks
                PSD via Cholesky decomposition; if your edits break it, a warning appears with
                a <strong>Fix Matrix</strong> button that runs Higham's nearest-PD algorithm to
                snap the matrix to the closest valid one (preserving as much of your intent
                as possible).
              </p>
            </div>

            {/* ─── Compute from History ──────────────────────────────────── */}
            <div id="cma-guide-compute" style={sectionStyle}>
              <h5 style={h5Style}>Compute from History — the pipeline</h5>
              <p>
                This panel (in the Covariance Matrix tab) builds the matrix from
                <code style={codeStyle}>monthly_historic_returns</code> using a deterministic
                seven-step pipeline. It's designed to produce statistically defensible
                correlations while still letting you inject forward-looking scenario views.
              </p>

              <h6 style={h6Style}>Step 1 — Load historical index levels</h6>
              <p>
                Reads <code style={codeStyle}>monthly_historic_returns</code> from the blender
                database, filtered to the SACs in the current scenario. Pivots to a wide
                DataFrame indexed by date.
              </p>

              <h6 style={h6Style}>Step 2 — Apply the history window</h6>
              <p>
                Truncates the panel to the start/end dates you specified. Defaults to
                1990-01-01 → today. Shortening the window is useful if you want to weight
                more recent regimes more heavily, or exclude the GFC if you believe it's not
                representative of the forward-looking environment.
              </p>

              <h6 style={h6Style}>Step 3 — Convert to returns</h6>
              <p>
                The table stores price/index levels, not returns. The pipeline calls
                <code style={codeStyle}>pct_change()</code> internally so you work in
                returns from here onwards.
              </p>

              <h6 style={h6Style}>Step 4 — Stambaugh backfill (optional)</h6>
              <p>
                Many SACs have shorter histories than others — Private Equity, EM, REITs,
                Infrastructure and alternatives often start 2000+ while equity/bond
                series go back to 1990. A naive <code style={codeStyle}>dropna()</code>
                would either truncate the panel to the shortest series (wasting a decade
                of equity/bond data) or leave NaN gaps that prevent shrinkage.
              </p>
              <p>
                <strong>Stambaugh backfill</strong> fixes this by extending the short series
                backwards. For each SAC with <em>backfill proxies</em> configured (set in the
                Sub Asset Classes tab), the pipeline:
              </p>
              <ol>
                <li>Finds months where the target SAC and ALL its proxies have data (the overlap).</li>
                <li>If the overlap is at least 60 months, fits OLS: <code style={codeStyle}>target ~ intercept + Σ β<sub>i</sub> · proxy<sub>i</sub></code>.</li>
                <li>For each pre-history month where the proxies have data but the target doesn't, predicts the target value from the regression.</li>
                <li>Splices the predicted values into the panel.</li>
              </ol>
              <p>
                <em>Caveat:</em> backfilled values are conditional means — they understate
                variance because the residual is omitted. For correlation estimation this
                matters less than for vol estimation (we override vols anyway from the
                scenario's Expected Returns).
              </p>
              <p>
                <em>How to configure:</em> in the Sub Asset Classes tab, the
                <strong> Backfill Proxies</strong> column accepts a comma-separated list of
                SAC codes. Example: for <code style={codeStyle}>PE</code>, set proxies to
                <code style={codeStyle}> EQ_AU, EQ_WORLD</code> — pre-1999 PE returns will then
                be synthesised from those equity series. Empty = no backfill (the SAC uses
                only its actual history).
              </p>

              <h6 style={h6Style}>Step 5 — Ledoit-Wolf shrinkage</h6>
              <p>
                Computes the base correlation matrix on the (now mostly-rectangular) panel
                using <strong>Ledoit-Wolf shrinkage</strong>. Sample correlations from finite
                history are noisy — particularly off-diagonals between similar assets. LW
                pulls the sample correlation matrix toward a structured target (sklearn's
                LW shrinks toward scalar identity) by an amount the data optimally chooses.
                The result is a less noisy, more stable correlation estimate.
              </p>
              <p>
                The <strong>shrinkage intensity</strong> (shown in diagnostics) tells you how
                much the data wanted to shrink:
              </p>
              <ul>
                <li><code style={codeStyle}>&lt; 0.10</code> — barely any shrinkage; sample is large enough that LW trusts the raw correlations.</li>
                <li><code style={codeStyle}>0.10 – 0.40</code> — typical for monthly asset-class returns. Healthy zone.</li>
                <li><code style={codeStyle}>&gt; 0.50</code> — the data is asking for a lot of regularisation. Could indicate small effective sample size or very noisy series.</li>
              </ul>

              <h6 style={h6Style}>Step 6 — Regime-conditional blending (optional)</h6>
              <p>
                Sample correlations average across regimes and dilute regime-specific
                behaviour. In a "Sticky Inflation" scenario, you might believe bonds and
                equities should co-move positively (as in 2022–24), not the long-run
                historical ~−0.1. Regime blending lets you express that view directly using
                real data.
              </p>
              <p>
                <strong>How it works:</strong>
              </p>
              <ol>
                <li>You define macro regimes in the <strong>Macro Regimes</strong> admin tab — each is a name + date window (e.g. <em>Sticky Inflation 2021–24</em>, <em>Post-GFC Low Inflation 2009–20</em>).</li>
                <li>In the Compute from History panel, you set a weight per regime (e.g. <code style={codeStyle}>Sticky Inflation: 0.4, Normal: 0.6</code>).</li>
                <li>The pipeline subsets the returns panel to in-regime months for each weighted regime, computes a correlation matrix per regime, and blends them as a weighted average.</li>
                <li>Regimes with fewer than 60 in-sample months are automatically dropped from the blend (with a warning in diagnostics) so noisy regime estimates don't pollute the final matrix.</li>
              </ol>
              <p>
                Leave all weights blank to use the full-history correlation from step 5.
                Regimes may overlap in time — the blender intersects each regime's date
                window with the panel independently.
              </p>

              <h6 style={h6Style}>Step 7 — Multiplicative tilts (optional)</h6>
              <p>
                Sometimes you want a sharper, more surgical view on a specific pair. The
                tilts subsection lets you multiply specific correlations by a factor:
              </p>
              <p>
                <code style={codeStyle}>
                  ρ<sub>new</sub>(i, j) = clip(ρ<sub>blend</sub>(i, j) × multiplier(i, j), −0.99, +0.99)
                </code>
              </p>
              <p>
                Example: set <em>EQ_AU ↔ BND_AU = 2.0</em> in a stagflation scenario to double
                whatever historical correlation the previous steps produced. The clip prevents
                runaway tilts from producing degenerate ±1 cells.
              </p>

              <h6 style={h6Style}>Step 8 — PSD repair</h6>
              <p>
                Tilts (and large regime weights) can easily break PSD. The pipeline always
                finishes with a repair: it eigendecomposes the matrix, clamps any negative
                eigenvalues to a tiny positive value, and rescales the diagonal back to unit
                correlation. The diagnostics report:
              </p>
              <ul>
                <li><strong>Min eigenvalue before/after</strong> — how non-PSD the matrix was. A large negative number before repair means your overlay was aggressive.</li>
                <li><strong>Frobenius distance</strong> — how far the repaired matrix moved from your requested one (overall). Anything above 0.1 triggers a "large repair" badge — your overlay was largely redistributed.</li>
                <li><strong>Per-pair tilt outcomes</strong> — for each tilt you requested, this table shows the base correlation, multiplier, requested value, post-repair value, and the delta from PSD redistribution. If a tilt moved by more than 0.05 in repair, a warning marker appears next to it.</li>
              </ul>

              <h6 style={h6Style}>Step 9 — Scale by scenario vols</h6>
              <p>
                The final covariance is built as
                <code style={codeStyle}>Σ = D · ρ · D</code> where
                <code style={codeStyle}>D = diag(annual vol per SAC)</code> from Expected
                Returns. This guarantees that the saved covariance has the exact scenario
                vols on its diagonal — the historical pipeline never overrides your
                forward-looking vol views.
              </p>

              <h6 style={h6Style}>Preview, save, discard</h6>
              <ul>
                <li><strong>Preview</strong> runs the pipeline and overlays the result on the matrix grid in light blue. Nothing is saved.</li>
                <li><strong>Save to Scenario</strong> persists the previewed (or freshly computed) matrix into <code style={codeStyle}>CMACovariance</code> for the scenario. It also records a <code style={codeStyle}>CMACovarianceAudit</code> row with the exact overlay parameters and full diagnostics — so any future question of "why did this matrix change?" is reproducible.</li>
                <li><strong>Discard Preview</strong> clears the overlay and reverts the grid to the persisted matrix.</li>
              </ul>
            </div>

            {/* ─── Downstream usage ──────────────────────────────────────── */}
            <div id="cma-guide-downstream" style={sectionStyle}>
              <h5 style={h5Style}>How CMAs flow to the rest of the Portal</h5>
              <p>
                Once a scenario is saved, every downstream consumer reads its returns and
                covariance from the database. Key consumers:
              </p>
              <ul>
                <li><strong>Portfolio Optimiser</strong> — uses Σ and μ to find efficient frontiers and run mean-variance optimisation. Choice of scenario is the single biggest driver of optimiser output, far more so than the choice of solver.</li>
                <li><strong>Portfolio Lab</strong> — uses Σ for risk decomposition (vol contributions per SAC, tracking error).</li>
                <li><strong>Portfolio Reports</strong> — uses μ and Σ for expected-return projections and confidence intervals.</li>
                <li><strong>Retirement Calculator</strong> — uses μ and σ for Monte Carlo wealth projections.</li>
                <li><strong>Blender stress tests</strong> — use the historical returns directly (not the forward-looking CMA), so changes here don't affect the stress test path.</li>
              </ul>
              <p>
                When a consumer doesn't specify a scenario explicitly, it uses the
                <strong> default</strong> scenario. Promoting a new scenario to default
                therefore changes optimisation outputs across the entire Portal —
                worth confirming with stakeholders before doing so in production.
              </p>
            </div>

            {/* ─── Recommended workflow ──────────────────────────────────── */}
            <div id="cma-guide-workflow" style={sectionStyle}>
              <h5 style={h5Style}>Recommended workflow for a new scenario</h5>
              <ol>
                <li>Click <strong>+ New Scenario</strong>, name it (e.g. <em>Sticky Inflation 2026</em>), and clone from your current default to inherit a starting point.</li>
                <li>Open the <strong>Expected Returns</strong> tab. Adjust the return / vol per SAC to reflect the forward-looking view. Save.</li>
                <li>Open the <strong>Covariance Matrix</strong> tab. Click <strong>Compute from History</strong>.</li>
                <li>Choose your <strong>history window</strong>. For most scenarios, 1990-01-01 → today is sensible. Shorten it (e.g. to 2010) if you want to emphasise the post-GFC regime.</li>
                <li>If you've configured <strong>backfill proxies</strong> for short-history SACs (PE, EM, etc.), tick <strong>Stambaugh backfill</strong>.</li>
                <li>For regime-aware scenarios, set <strong>regime weights</strong>. E.g. for Sticky Inflation: <code style={codeStyle}>Sticky Inflation: 0.5, Normal: 0.5</code>.</li>
                <li>For surgical overrides, add <strong>pair tilts</strong>. Most scenarios won't need any — use sparingly.</li>
                <li>Click <strong>Preview</strong>. Inspect the diagnostics block: shrinkage intensity, regime month counts, tilt deltas, eigenvalues, and Frobenius distance. Verify the matrix looks economically sensible (equity cluster correlations 0.7–0.85, credit cluster high, equity-bond near your target).</li>
                <li>If happy, click <strong>Save to Scenario</strong>. The matrix is persisted and an audit row is written. If not, adjust regime weights or tilts and Preview again.</li>
              </ol>
            </div>

            {/* ─── Troubleshooting ───────────────────────────────────────── */}
            <div id="cma-guide-troubleshooting" style={sectionStyle}>
              <h5 style={h5Style}>Troubleshooting</h5>

              <h6 style={h6Style}>"No expected returns data for this scenario"</h6>
              <p>
                The scenario was created empty. Either clone from another scenario when
                creating, use <strong>Import from Blender DB</strong>, or paste the returns
                from Excel.
              </p>

              <h6 style={h6Style}>Compute panel says <em>"monthly_historic_returns is empty"</em></h6>
              <p>
                You haven't saved your Index Mappings yet, or no mappings have been configured.
                Open the <strong>Index Mappings</strong> tab, map at least the core SACs to
                their tactical DB index columns, and click Save. That populates
                <code style={codeStyle}>monthly_historic_returns</code> in the blender DB.
              </p>

              <h6 style={h6Style}>Equity-cluster correlations look too low (~0.3)</h6>
              <p>
                You're seeing the multicollinearity-collapse artefact from the old offline
                pipeline. The in-app pipeline uses sample-based shrinkage rather than
                leave-one-out regression, so this should not happen — if it does, check that
                the SACs in question actually have overlapping history (per-SAC first-valid-date
                in diagnostics) and that you're not blending in a regime with too few months.
              </p>

              <h6 style={h6Style}>Tilt "Δ from repair" is large</h6>
              <p>
                Your tilt was aggressive enough that the PSD repair had to spread it across
                other cells to keep the matrix valid. Either accept the repaired value, or
                back off the multiplier. A single tilt rarely lands cleanly because changing
                one correlation has implications for all the others.
              </p>

              <h6 style={h6Style}>Frobenius distance &gt; 0.1 in PSD repair diagnostics</h6>
              <p>
                The overall amount of movement from your requested matrix to the PSD-repaired
                one is large. Usually this means the combination of tilts and regime weights
                produced an internally inconsistent matrix. Try reducing the magnitude of the
                most aggressive tilt first.
              </p>

              <h6 style={h6Style}>Ledoit-Wolf shrinkage intensity is very high (&gt;0.6)</h6>
              <p>
                The shrinkage is doing a lot of work, meaning the sample correlation is very
                noisy relative to the shrinkage target. This usually means your history window
                is too short, or there's a lot of NaN-heavy SACs in the panel. Extend the
                history window, or add backfill proxies to the offending SACs.
              </p>
            </div>

          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CMAUserGuideModal;
